import { CheckForAvailableFundsModal } from 'components/order/escrow/escrowCreateEdit/checkForAvailableFundsModal/CheckForAvailableFundsModal';
import { useField } from 'formik';
import React, { useCallback, useState } from 'react';
import { ButtonTW } from 'tailwindComponents/buttonTW/ButtonTW';
import ReactDOM from 'react-dom';
import { ModalTarget } from '../../lineItem/ModalTarget.Constant';
import { useGetEscrowBalancesDataQuery } from 'API/order/escrow/EscrowBaseAPI';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { assertDefined } from 'utilities/Assertions';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { Tooltip } from 'ui-toolkit';
import { IInternalTransferDisbursementFormData } from 'types/order/escrow/internalTransferDisbursements/IInternalTransferDisbursementFormData';
import { useSendInternalTransferDisbursementMutation } from 'API/order/escrow/InternalTransferDisbursementAPI';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { useHasPermissions } from '../../../../../accessControl/hooks/useHasPermissions';

export interface ISendInternalTransferButtonProps {
  baseName: string;
  internalTransfer: IInternalTransferDisbursementFormData;
  disabled?: boolean;
}

export const SendInternalTransferButton: React.FC<ISendInternalTransferButtonProps> = ({
  baseName,
  disabled,
  internalTransfer,
}) => {
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);
  const hasDisbursePermissions = useHasPermissions([ePermissions.EditEscrowInitiateDisbursement]);

  const { data } = useGetEscrowBalancesDataQuery(orderId);
  const [onSend] = useSendInternalTransferDisbursementMutation();
  const [{ value: amountCents }] = useField<number | undefined>(`${baseName}.amountCents`);
  const [{ value: holdback }] = useField<boolean | undefined>(`${baseName}.holdback`);

  const hasEnoughMoney = Math.round((data?.availableAmountDollars ?? 0) * 100) >= Math.round(amountCents ?? 0);
  const [showNotEnoughWarning, setShowNotEnoughWarning] = useState<boolean>(false);

  const onSendInternalTransferDisbursement = useCallback(async () => {
    const sendResult = await onSend({ internalTransferId: internalTransfer.id, orderId });
    checkForMutationSuccess(sendResult, 'sending internal transfer');
  }, [orderId, internalTransfer.id, onSend]);

  const onStartSendProcess = useCallback(async () => {
    if (hasEnoughMoney) {
      await onSendInternalTransferDisbursement();
    } else {
      setShowNotEnoughWarning(true);
    }
  }, [hasEnoughMoney, setShowNotEnoughWarning, onSendInternalTransferDisbursement]);

  const onIgnoreWarning = useCallback(async () => {
    setShowNotEnoughWarning(false);
    await onSendInternalTransferDisbursement();
  }, [onSendInternalTransferDisbursement, setShowNotEnoughWarning]);

  const targetElement = document.getElementById(ModalTarget);

  return (
    <>
      {hasDisbursePermissions && (
        <Tooltip
          placement={'auto'}
          trigger={disabled || holdback ? 'hover' : 'none'}
          tooltipContent={holdback ? 'Cannot send due to holdback' : 'Please save pending changes'}
        >
          <ButtonTW
            type={'button'}
            onClick={onStartSendProcess}
            data-test-id={`${baseName}.send-internal-transfer-button`}
            disabled={disabled || holdback}
          >
            Send
          </ButtonTW>
        </Tooltip>
      )}
      {showNotEnoughWarning &&
        targetElement &&
        ReactDOM.createPortal(
          <CheckForAvailableFundsModal
            submitData={onIgnoreWarning}
            type={'internalTransfer'}
            handleClose={() => setShowNotEnoughWarning(false)}
          />,
          targetElement
        )}
      {showNotEnoughWarning && !targetElement && (
        <CheckForAvailableFundsModal
          submitData={onIgnoreWarning}
          type={'internalTransfer'}
          handleClose={() => setShowNotEnoughWarning(false)}
        />
      )}
    </>
  );
};
